import type { DispatchMetricPoint, DispatchMetricSeries } from '../../api/types';
import { civilParts } from '../../lib/timeZone';
import { type Bucket, type CivilDay, addDays, addMonths, dayString, daysBetween } from './frames';
import {
  type ValueKind,
  isSnapshotSubcategory,
  seriesMatches,
  sharedValueKind,
  subcategoryLabel,
  usesSum,
  valueKindOf,
} from './metricMeta';

/**
 * dispatch's answer, turned into something a chart can draw.
 *
 * Three jobs, and the middle one is the awkward one.
 *
 * **Pick the right number.** A point's `total` is the record count. Money the
 * API pre-sums lives in `sum`. Jobs Total is the exception: its dollars are on
 * each record, and the figure is their sum. A count snapshot with no `total`
 * falls back to how many records came back.
 *
 * **Put the buckets back that dispatch left out.** It answers only the buckets
 * that had something in them, so thirty days with ten quiet ones come back as
 * twenty points. Drawn as they arrive those twenty are evenly spaced, which
 * reads as twenty consecutive days and makes a quiet week look like a busy
 * one. Every gap is filled with a zero.
 *
 * **Line the series up.** Two subcategories on one card come back as two
 * independent lists with different gaps in them, so they have to be projected
 * onto one shared set of buckets before anything can be drawn or stacked.
 *
 * The grid is generated from the CALENDAR for days, months, quarters and
 * years, which have no convention to disagree about. Weeks do — a week may
 * start on Sunday or Monday depending on who is counting — so the weekly grid
 * is anchored on the earliest bucket dispatch actually returned and stepped
 * seven days from there. That way the x-axis is dispatch's own week grid
 * discovered from the data, rather than this file guessing at it and labelling
 * every bar up to six days out.
 */

/** dispatch sends this instead of a timestamp on the all-time snapshot buckets. */
export const SNAPSHOT_PERIOD = 'all';

export interface SeriesShape {
  code: string;
  label: string;
  kind: ValueKind;
}

export interface BucketRow {
  key: string;
  /** Short axis tick. The tooltip carries the real date in the tenant's format. */
  label: string;
  start: Date;
  /** One figure per series, in the order of `ChartData.series`. */
  values: number[];
}

export interface ChartData {
  series: SeriesShape[];
  buckets: BucketRow[];
  /** Per series, over the whole window. */
  totals: number[];
  /** Null when the card mixes money and counts, which has no single axis. */
  kind: ValueKind | null;
  /** Every series empty — told apart from a read that failed, which never gets here. */
  empty: boolean;
}

export interface KpiFigure {
  current: number;
  /** Null when there is nothing to compare against: a snapshot has no previous. */
  previous: number | null;
  kind: ValueKind;
  snapshot: boolean;
}

const finiteNumber = (raw: unknown): number | null => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }
  return null;
};

/** Jobs Total's dollars: each record carries `total`, and the point's does not. */
const sumRecordField = (data: DispatchMetricPoint['data'], field: string): number => {
  if (!Array.isArray(data)) return 0;
  let sum = 0;
  for (const record of data) {
    if (!record || typeof record !== 'object') continue;
    const value = finiteNumber(record[field]);
    if (value !== null) sum += value;
  }
  return Number.isFinite(sum) ? sum : 0;
};

/**
 * A count snapshot may omit `total`. A positive one wins; otherwise the
 * records that arrived are the count. Currency snapshots must not come here —
 * their figure is an amount, not a number of rows.
 */
const snapshotCount = (point: DispatchMetricPoint): number => {
  const total = finiteNumber(point.total);
  if (total !== null && total > 0) return total;
  return Array.isArray(point.data) ? point.data.length : 0;
};

/**
 * The figure on a point, the same four ways dispatch's dashboard reads one.
 *
 * Counts read `total`. Count snapshots fall back to the record list when that
 * is missing. Currency the API pre-sums reads `sum`. Jobs Total sums each
 * record's `total`, because the point's `total` is the job count.
 */
export const valueOf = (point: DispatchMetricPoint, code: string): number => {
  if (valueKindOf(code) === 'count') {
    if (isSnapshotSubcategory(code)) return snapshotCount(point);
    return finiteNumber(point.total) ?? 0;
  }
  if (usesSum(code)) return finiteNumber(point.sum) ?? 0;
  return sumRecordField(point.data, 'total');
};

const seriesFor = (rows: DispatchMetricSeries[], code: string): DispatchMetricSeries | undefined =>
  rows.find((row) => seriesMatches(code, String(row.subcategory)));

/** Everything in the window, for a KPI's headline and a chart's per-series total. */
export const sumOf = (rows: DispatchMetricSeries[], code: string): number => {
  const row = seriesFor(rows, code);
  if (!row) return 0;
  return row.value.reduce((running, point) => running + valueOf(point, code), 0);
};

/* ---- bucketing ------------------------------------------------------------ */

const civilOf = (date: Date, zone: string | null): CivilDay => {
  const parts = civilParts(date, zone);
  return { y: parts.year, m: parts.month, d: parts.day };
};

const quarterOf = (day: CivilDay): number => Math.floor((day.m - 1) / 3) + 1;

/**
 * The bucket a point belongs to.
 *
 * Resolved from the point's own instant in the TENANT's zone, never the
 * viewer's: a job at 23:30 in New York is that day's job for the office that
 * ran it, and bucketing it in the reader's zone moves it to tomorrow for
 * anyone reading from Europe.
 */
const keyOf = (date: Date, bucket: Bucket, zone: string | null, weekAnchor: number | null): string => {
  const day = civilOf(date, zone);
  switch (bucket) {
    case 'day':
      return dayString(day);
    case 'week': {
      if (weekAnchor === null) return dayString(day);
      const index = Math.floor((dayIndexOf(day) - weekAnchor) / 7);
      return `w${index}`;
    }
    case 'month':
      return `${day.y}-${String(day.m).padStart(2, '0')}`;
    case 'quarter':
      return `${day.y}-Q${quarterOf(day)}`;
    default:
      return String(day.y);
  }
};

const dayIndexOf = (day: CivilDay): number => Math.round(Date.UTC(day.y, day.m - 1, day.d) / 86_400_000);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The short tick under a bar.
 *
 * This is the one place on this screen that formats a date outside
 * `useTimeFormat()`, and it is the same exception `shortDay` takes on the
 * analytics screen for the same reason: an axis tick is not a timestamp.
 * Twelve of the tenant's full patterns across one card is unreadable, and the
 * tick has no instant in it to put in a zone by the time it is drawn. Every
 * date a reader can act on — the tooltip, the range in the header — goes
 * through the tenant's own patterns.
 */
export const bucketLabel = (day: CivilDay, bucket: Bucket): string => {
  switch (bucket) {
    case 'day':
      return `${day.d} ${MONTHS[day.m - 1]}`;
    case 'week':
      return `${day.d} ${MONTHS[day.m - 1]}`;
    case 'month':
      return `${MONTHS[day.m - 1]} ${String(day.y).slice(2)}`;
    case 'quarter':
      return `Q${quarterOf(day)} ${String(day.y).slice(2)}`;
    default:
      return String(day.y);
  }
};


/**
 * Every bucket the window covers, in order — including the empty ones.
 *
 * `anchorDays` are the civil days of the buckets dispatch returned, and they
 * matter only for weeks: the weekly grid starts at the earliest of them so the
 * bars land on dispatch's week boundaries rather than on a convention invented
 * here. With nothing returned at all the window's first day stands in, which
 * draws an empty but correctly-spaced axis.
 */
export const gridFor = (
  from: CivilDay,
  to: CivilDay,
  bucket: Bucket,
  anchorDays: CivilDay[],
): { key: string; day: CivilDay }[] => {
  const out: { key: string; day: CivilDay }[] = [];

  if (bucket === 'week') {
    const anchor = anchorDays.length
      ? anchorDays.reduce((earliest, day) => (dayIndexOf(day) < dayIndexOf(earliest) ? day : earliest))
      : from;
    const anchorIndex = dayIndexOf(anchor);
    // Walk back to the first week of the grid that still touches the window,
    // then forward past the end — a window opening mid-week must still show
    // the week it opened in.
    let index = Math.floor((dayIndexOf(from) - anchorIndex) / 7);
    const last = Math.floor((dayIndexOf(to) - anchorIndex) / 7);
    for (; index <= last; index += 1) {
      out.push({ key: `w${index}`, day: addDays(anchor, index * 7) });
    }
    return out;
  }

  if (bucket === 'day') {
    const count = daysBetween(from, to);
    for (let step = 0; step < count; step += 1) {
      const day = addDays(from, step);
      out.push({ key: dayString(day), day });
    }
    return out;
  }

  if (bucket === 'month') {
    let cursor: CivilDay = { y: from.y, m: from.m, d: 1 };
    const end = { y: to.y, m: to.m, d: 1 };
    while (cursor.y * 12 + cursor.m <= end.y * 12 + end.m) {
      out.push({ key: `${cursor.y}-${String(cursor.m).padStart(2, '0')}`, day: cursor });
      cursor = addMonths(cursor, 1);
    }
    return out;
  }

  if (bucket === 'quarter') {
    let cursor: CivilDay = { y: from.y, m: (quarterOf(from) - 1) * 3 + 1, d: 1 };
    const end = { y: to.y, m: (quarterOf(to) - 1) * 3 + 1, d: 1 };
    while (cursor.y * 12 + cursor.m <= end.y * 12 + end.m) {
      out.push({ key: `${cursor.y}-Q${quarterOf(cursor)}`, day: cursor });
      cursor = addMonths(cursor, 3);
    }
    return out;
  }

  for (let year = from.y; year <= to.y; year += 1) {
    out.push({ key: String(year), day: { y: year, m: 1, d: 1 } });
  }
  return out;
};

/* ---- assembling ----------------------------------------------------------- */

export interface BuildChartArgs {
  rows: DispatchMetricSeries[];
  codes: string[];
  from: CivilDay;
  to: CivilDay;
  bucket: Bucket;
  zone: string | null;
}

export const buildChart = ({ rows, codes, from, to, bucket, zone }: BuildChartArgs): ChartData => {
  const series: SeriesShape[] = codes.map((code) => ({
    code,
    label: subcategoryLabel(code),
    kind: valueKindOf(code),
  }));

  // Weeks need dispatch's own grid, so collect the days its buckets landed on
  // before deciding where the columns go.
  const anchorDays: CivilDay[] = [];
  for (const code of codes) {
    const row = seriesFor(rows, code);
    if (!row) continue;
    for (const point of row.value) {
      if (point.period === SNAPSHOT_PERIOD) continue;
      const date = new Date(point.period);
      if (!Number.isNaN(date.getTime())) anchorDays.push(civilOf(date, zone));
    }
  }

  const grid = gridFor(from, to, bucket, anchorDays);
  const weekAnchor = bucket === 'week' && grid.length ? dayIndexOf(grid[0].day) : null;
  // The grid's own keys are struck from its first bucket, so a point's key has
  // to be measured from the same origin or nothing would ever match.
  const slotOf = new Map(grid.map((slot, index) => [slot.key, index]));

  const values: number[][] = codes.map(() => grid.map(() => 0));
  const totals: number[] = codes.map(() => 0);

  codes.forEach((code, seriesIndex) => {
    const row = seriesFor(rows, code);
    if (!row) return;
    for (const point of row.value) {
      const figure = valueOf(point, code);
      totals[seriesIndex] += figure;
      if (point.period === SNAPSHOT_PERIOD) continue;
      const date = new Date(point.period);
      if (Number.isNaN(date.getTime())) continue;
      const slot = slotOf.get(keyOf(date, bucket, zone, weekAnchor));
      // A bucket outside the window is dispatch answering a little wider than
      // it was asked — counted in the total, not drawn off the end of the axis.
      if (slot === undefined) continue;
      values[seriesIndex][slot] += figure;
    }
  });

  const buckets: BucketRow[] = grid.map((slot, index) => ({
    key: slot.key,
    label: bucketLabel(slot.day, bucket),
    start: new Date(Date.UTC(slot.day.y, slot.day.m - 1, slot.day.d)),
    values: values.map((row) => row[index]),
  }));

  return {
    series,
    buckets,
    totals,
    kind: sharedValueKind(codes),
    empty: totals.every((total) => total === 0),
  };
};

/**
 * A KPI's two figures.
 *
 * A snapshot answers a running total with no window, so it has no previous
 * period and `previous` is null rather than zero — nothing to compare is a
 * different statement from "it was nothing before", and rendering the second
 * puts a green +100% on every outstanding-payables card.
 */
export const buildKpi = ({
  rows,
  compareRows,
  codes,
  snapshot,
}: {
  rows: DispatchMetricSeries[];
  compareRows: DispatchMetricSeries[] | null;
  codes: string[];
  snapshot: boolean;
}): KpiFigure => {
  const current = codes.reduce((running, code) => running + sumOf(rows, code), 0);
  const kind = sharedValueKind(codes) ?? valueKindOf(codes[0] ?? '');
  if (snapshot || !compareRows) {
    return { current, previous: null, kind, snapshot };
  }
  const previous = codes.reduce((running, code) => running + sumOf(compareRows, code), 0);
  return { current, previous, kind, snapshot: false };
};

/* ---- the x axis ----------------------------------------------------------- */

/** The tick face's size, and a serviceable width-per-character for it. */
const TICK_FONT = 11;
const TICK_CHAR = 6.2;

export interface AxisPlan {
  /** Degrees to rotate each tick. 0 is upright; -90 reads bottom-to-top. */
  angle: 0 | -45 | -90;
  /** The band the ticks need under the plot, so nothing is ever clipped. */
  pad: number;
  /** Label every nth bucket. 1 is every one, which is the point. */
  step: number;
}

/**
 * How to print the x axis so **every bucket is labelled**.
 *
 * The old rule printed about eight ticks whatever the window, which reads fine
 * and answers the wrong question: an operator looking at thirty days wants to
 * find the 14th, and a chart that labels the 1st, 5th, 9th makes them count
 * bars. So the labels are not thinned — the TEXT is turned instead, and the
 * band under the plot grows to fit what turning it costs.
 *
 * Upright while the slot fits the longest label, then 45°, then vertical. The
 * order is by legibility: upright is read fastest, and there is no reason to
 * turn text that fits.
 *
 * `step` above 1 is the floor rather than the plan — past roughly one character
 * of width per bucket even vertical text overlaps, and a smear of overlapping
 * dates is less readable than every third one. A year of days hits this; a
 * month never does.
 */
export const axisPlan = (labels: string[], slot: number): AxisPlan => {
  const longest = Math.max(1, ...labels.map((label) => label.length)) * TICK_CHAR;
  if (slot >= longest + 6) return { angle: 0, pad: 22, step: 1 };

  // Turned text is read along its own baseline, so what it needs ACROSS the
  // axis is its line height rather than its length — and what it needs BELOW
  // is its length, which is why the band grows with it.
  if (slot >= TICK_FONT * 1.5) return { angle: -45, pad: Math.ceil(longest * 0.71) + 14, step: 1 };
  if (slot >= TICK_FONT * 1.05) return { angle: -90, pad: Math.ceil(longest) + 12, step: 1 };
  return {
    angle: -90,
    pad: Math.ceil(longest) + 12,
    step: Math.max(1, Math.ceil((TICK_FONT * 1.05) / Math.max(slot, 1))),
  };
};

/**
 * Which buckets get a tick, given the plan — every one unless the floor bit,
 * ALWAYS the highlighted one, which is the whole reason it is highlighted, and
 * always the last, which is usually today.
 *
 * Those two are placed first and the regular ticks fill in around them: a
 * regular tick closer than `step` to either gives way, because `step` is
 * exactly the spacing below which two turned labels print over each other.
 * Appending the last bucket after the thinning had already ticked the one
 * beside it is how "22 Sep" and "23 Sep" came to share a smudge at the end of
 * every narrow thirty-day chart. Between the two, the highlight wins.
 */
export const tickAt = (count: number, plan: AxisPlan, highlight = -1): number[] => {
  const step = Math.max(1, plan.step);
  const clashes = (placed: number[], index: number) => placed.some((kept) => Math.abs(kept - index) < step);
  const fixed: number[] = [];
  for (const index of [highlight, count - 1]) {
    if (index >= 0 && index < count && !clashes(fixed, index)) fixed.push(index);
  }
  const out = [...fixed];
  for (let index = 0; index < count; index += step) {
    if (!clashes(fixed, index)) out.push(index);
  }
  return out.sort((a, b) => a - b);
};
